import React, { useState, useEffect } from "react"
import {
    Box,
    Typography,
    Button,
    TextField,
    CircularProgress,
} from "@mui/material"
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined"
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import SectionBox from "../../WorkforceManagement/components/SectionBox"
import WeeklyScheduleOverview from "../WeeklyScheduleOverview/WeeklyScheduleOverview"
import { getAllAttendanceSheets, getAttendanceSheet, createAttendanceSheet } from "../../../utils/RosterAPI"
import { toast } from "react-toastify"
import AttendanceSheetModal from "./AttendanceSheetModal"

export default function AttendanceSheetsTab({ rosterId }) {
    const [selectedDate, setSelectedDate] = useState("")
    const [attendanceSheets, setAttendanceSheets] = useState([])
    const [loading, setLoading] = useState(false)
    const [totalSheets, setTotalSheets] = useState(0)
    const [selectedSheet, setSelectedSheet] = useState(null)
    const [openModal, setOpenModal] = useState(false)
    const [loadingSheet, setLoadingSheet] = useState(false)

    const handleDateChange = (e) => setSelectedDate(e.target.value)

    const handleCreateSchedule = async () => {
        if (!selectedDate) {
            toast.error("Please select a start date")
            return
        }

        if (!rosterId) {
            toast.error("Roster ID is missing. Please create a roster first.")
            return
        }

        // Calculate week end date (6 days after start date)
        const startDate = new Date(selectedDate)
        const endDate = new Date(startDate)
        endDate.setDate(startDate.getDate() + 6)

        const weekStartDateStr = startDate.toISOString().split('T')[0]
        const weekEndDateStr = endDate.toISOString().split('T')[0]

        setLoading(true)
        try {
            const response = await createAttendanceSheet(
                rosterId,
                weekStartDateStr,
                weekEndDateStr
            )
            if (response) {
                toast.success(response.message || "Weekly attendance sheet created successfully")
                setSelectedDate("")
                // Refresh attendance sheets list
                await fetchAttendanceSheets()
            }
        } catch (error) {
            console.error("Error creating attendance sheet:", error)
            toast.error(error?.message || "Failed to create attendance sheet")
        } finally {
            setLoading(false)
        }
    }

    // Fetch attendance sheets on mount
    useEffect(() => {
        if (rosterId) {
            fetchAttendanceSheets()
        } else {
            // If no rosterId, clear attendance sheets
            setAttendanceSheets([])
            setTotalSheets(0)
        }
    }, [rosterId])

    const fetchAttendanceSheets = async () => {
        if (!rosterId) return
        
        setLoading(true)
        try {
            const response = await getAllAttendanceSheets(rosterId)
            if (response) {
                const sheets = response.attendanceSheets || []
                // Sort by creation date (most recent first)
                // Using createdAt, createdDate, or attendanceSheetId as fallback
                const sortedSheets = [...sheets].sort((a, b) => {
                    // Try createdAt first, then createdDate, then use attendanceSheetId as fallback (higher ID = more recent)
                    const dateA = a.createdAt || a.createdDate || null
                    const dateB = b.createdAt || b.createdDate || null
                    
                    if (dateA && dateB) {
                        return new Date(dateB) - new Date(dateA)
                    }
                    // Fallback to attendanceSheetId if no date available (assuming higher ID = more recent)
                    return (b.attendanceSheetId || 0) - (a.attendanceSheetId || 0)
                })
                
                setAttendanceSheets(sortedSheets)
                setTotalSheets(response.totalSheets || sortedSheets.length)
            }
        } catch (error) {
            console.error("Error fetching attendance sheets:", error)
            toast.error(error?.message || "Failed to fetch attendance sheets")
        } finally {
            setLoading(false)
        }
    }

    const formatWeekRange = (startDate, endDate) => {
        if (!startDate || !endDate) return ""
        const start = new Date(startDate)
        const end = new Date(endDate)
        const fmt = (d) => {
            const day = String(d.getDate()).padStart(2, "0")
            const month = String(d.getMonth() + 1).padStart(2, "0")
            const year = d.getFullYear()
            return `${day}/${month}/${year}`
        }
        return `Week ${fmt(start)} - ${fmt(end)}`
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return ""
        const date = new Date(dateStr)
        const day = String(date.getDate()).padStart(2, "0")
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const year = date.getFullYear()
        return `${day}/${month}/${year}`
    }

    const mostRecent = attendanceSheets.length > 0 ? attendanceSheets[0] : null

    const handleViewSheet = async (attendanceSheetId) => {
        setLoadingSheet(true)
        try {
            const response = await getAttendanceSheet(attendanceSheetId)
            if (response) {
                setSelectedSheet(response)
                setOpenModal(true)
            }
        } catch (error) {
            console.error("Error fetching attendance sheet:", error)
            toast.error(error?.message || "Failed to fetch attendance sheet details")
        } finally {
            setLoadingSheet(false)
        }
    }

    const getWeekRange = (dateStr) => {
        if (!dateStr) return ""
        const date = new Date(dateStr)
        const start = new Date(date)
        start.setDate(date.getDate() - date.getDay() + 1)
        const end = new Date(start)
        end.setDate(start.getDate() + 6)

        const fmt = (d) =>
            `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`
        return `Week: ${fmt(start)} - ${fmt(end)}`
    }

    return (
        <>
            {/* ======= Section 1: Attendance Sheets ======= */}
            <SectionBox
                icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 22, color: "#101828" }} />}
                title="Attendance Sheets"
                titleSize="24px"
            >
                {/* ATTENDANCE LIST */}
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : attendanceSheets.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                        <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                            No attendance sheets found. Create a new schedule to get started.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        {attendanceSheets.map((sheet) => (
                            <Box
                                key={sheet.attendanceSheetId}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    border: "1px solid #E4E7EC",
                                    borderRadius: "8px",
                                    padding: "1rem 1.2rem",
                                    backgroundColor: "#fff",
                                    transition: "all 0.2s ease",
                                    "&:hover": { backgroundColor: "#F9FAFB" },
                                }}
                            >
                                {/* LEFT SIDE */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                color: "#101828",
                                                fontSize: "14px",
                                                mb: "2px",
                                            }}
                                        >
                                            {formatWeekRange(sheet.weekStartDate, sheet.weekEndDate)}
                                        </Typography>
                                        {sheet.sharedAt && (
                                            <Typography sx={{ color: "#667085", fontSize: "13px" }}>
                                                Shared: {formatDate(sheet.sharedAt)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                {/* RIGHT SIDE */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                                    <Button
                                        variant="text"
                                        startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                                        onClick={() => handleViewSheet(sheet.attendanceSheetId)}
                                        disabled={loadingSheet}
                                        sx={{
                                            textTransform: "none",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            color: "#344054",
                                            border: "none",
                                            boxShadow: "none",
                                            "&:hover": { backgroundColor: "#4D6BF6", color: "#fff" },
                                        }}
                                    >
                                        View
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </SectionBox>

            {/* ======= Section 2: Most Recent Schedule ======= */}
            <SectionBox
                icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 22, color: "#101828" }} />}
                title="Most Recent Schedule"
                titleSize="24px"
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: "1rem",
                    }}
                >
                    <Typography sx={{ fontWeight: 600, color: "#101828", fontSize: "18px" }}>
                        Most Recent Schedule
                    </Typography>

                    <Button
                        variant="outlined"
                        startIcon={<DownloadOutlinedIcon />}
                        sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            color: "#101828",
                            borderColor: "#E4E7EC",
                            border: "1px solid #E4E7EC",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.5rem",
                            padding: "0.5rem 1rem",
                            "&:hover": { borderColor: "#D0D5DD" },
                        }}
                    >
                        Download
                    </Button>
                </Box>

                {/* Recent schedule card */}
                {mostRecent ? (
                    <Box
                        onClick={() => handleViewSheet(mostRecent.attendanceSheetId)}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderRadius: "8px",
                            backgroundColor: "#f1f1f1",
                            p: "1rem 1.2rem",
                            mb: "1.8rem",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                                backgroundColor: "#E4E7EC",
                                transform: "translateY(-2px)",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            },
                        }}
                    >
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#101828" }}>
                                {formatWeekRange(mostRecent.weekStartDate, mostRecent.weekEndDate)}
                            </Typography>
                            {mostRecent.sharedAt && (
                                <Typography sx={{ color: "#667085", fontSize: "13px", mt: 0.5 }}>
                                    Shared: {formatDate(mostRecent.sharedAt)}
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                            <VisibilityOutlinedIcon 
                                sx={{ 
                                    fontSize: 18, 
                                    color: "#667085",
                                    "&:hover": { color: "#4D6BF6" }
                                }} 
                            />
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            borderRadius: "8px",
                            backgroundColor: "#f1f1f1",
                            p: "1rem 1.2rem",
                            mb: "1.8rem",
                            textAlign: "center",
                        }}
                    >
                        <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                            No attendance sheets available
                        </Typography>
                    </Box>
                )}

                {/* Create New Schedule */}
                <Box>
                    <Typography
                        sx={{ fontWeight: 600, color: "#101828", fontSize: "14px", mb: "0.5rem" }}
                    >
                        Create New Schedule
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "2rem",
                            flexWrap: "wrap",
                        }}
                    >
                        <TextField
                            type="date"
                            size="small"
                            value={selectedDate}
                            onChange={handleDateChange}
                            disabled={loading}
                            label="Week Start Date"
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                width: "200px",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontSize: "13px",
                                },
                            }}
                        />
                        <Typography sx={{ fontSize: "13px", color: "#344054" }}>
                            {getWeekRange(selectedDate) || "Select a start date"}
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleCreateSchedule}
                            disabled={loading || !selectedDate}
                            sx={{
                                backgroundColor: "#4D6BF6",
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 500,
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#3D5BD6" },
                                "&:disabled": { backgroundColor: "#E4E7EC", color: "#667085" },
                            }}
                        >
                            {loading ? "Creating..." : "Create"}
                        </Button>
                    </Box>
                </Box>
            </SectionBox>

            {/* Only show WeeklyScheduleOverview if there's a most recent sheet */}
            {mostRecent && (
                <WeeklyScheduleOverview 
                    attendanceSheetId={mostRecent.attendanceSheetId}
                    weekStartDate={mostRecent.weekStartDate}
                    weekEndDate={mostRecent.weekEndDate}
                    rosterId={rosterId}
                />
            )}

            {/* Attendance Sheet Modal */}
            <AttendanceSheetModal
                open={openModal}
                onClose={() => {
                    setOpenModal(false)
                    setSelectedSheet(null)
                }}
                attendanceSheet={selectedSheet}
            />
        </>
    )
}
